import {
  CarrierType,
  RailcarResponseDto,
  RailcarTrackPositionResponseDto,
  RailTrackResponseDto,
  WeightMeasurementUnit,
} from '@planning/app/api'
import httpClient from '@planning/app/http-client/http-client'
import { ImdgCodes } from '@planning/constants'
import { IGeneralCargoUploadOrderDto } from '@planning/pages/Order/stores/OrderListUploadViewStoreV2'
import { FileFormatError } from '@planning/pages/VesselVisit/Components/Upload/hooks/useOrderListUploadError'
import railcarService from '@planning/services/railcarService'
import Papa from 'papaparse'

interface IBillingCommodity {
  id: number
  code: string
  name?: string
  catalogueId: number
}

interface IBillingCommodityCatalogue {
  id: number
  name: string
}

class GeneralCargoOrderUploadParsingService {
  private readonly toCellString = (value: unknown): string => {
    if (value == null) return ''
    if (
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean' ||
      value instanceof Date
    ) {
      return String(value)
    }

    return ''
  }

  private readonly getBillingCommodities = async (): Promise<IBillingCommodity[]> => {
    const { data } = await httpClient.get<IBillingCommodity[]>('/billingService/api/Commodities')
    return data
  }

  private readonly getBillingCommodityCatalogues = async (): Promise<
    IBillingCommodityCatalogue[]
  > => {
    const { data } = await httpClient.get<IBillingCommodityCatalogue[]>(
      '/billingService/api/CommodityCatalogues',
    )
    return data
  }

  private readonly getRailcars = async (): Promise<RailcarResponseDto[]> => {
    const data = await railcarService.get(1, 10000)
    return data.payload ?? []
  }

  private async parseData(upload: File) {
    return await new Promise((resolve, reject) => {
      try {
        Papa.parse(upload, {
          dynamicTyping: false,
          header: true,
          skipEmptyLines: true,
          complete: r => {
            resolve(r.data)
          },
        })
      } catch (e) {
        reject(e)
      }
    })
  }

  private readonly parseSlashOrPipeSeparatedArray = (str?: string) =>
    str
      ?.split(/[/|]/)
      .map(item => item.trim())
      .filter(item => item.length > 0) ?? []

  private readonly mapWeightUnit = (raw?: string): WeightMeasurementUnit | undefined => {
    if (!raw) return undefined

    const normalized = raw.trim().toLowerCase()
    if (normalized === 'kg' || normalized === 'kilogram') return WeightMeasurementUnit.Kilogram
    if (normalized === 't' || normalized === 'ton') return WeightMeasurementUnit.Ton

    throw new FileFormatError(`Unsupported weight unit "${raw}". Allowed values are kg or t`)
  }

  private parsePositiveInteger(value: unknown, fieldName: string, rowIndex: number): number {
    const parsed = Number(value)
    if (!Number.isInteger(parsed) || parsed <= 0) {
      throw new FileFormatError(`${fieldName} must be a positive integer at row ${rowIndex}`)
    }

    return parsed
  }

  parseCsv = async (
    upload: File,
    visitType: CarrierType,
    railTrackNameMap?: Map<string, RailTrackResponseDto>,
    relatedRailcars?: RailcarTrackPositionResponseDto[],
  ): Promise<IGeneralCargoUploadOrderDto[]> => {
    const data = (await this.parseData(upload)) as Record<string, unknown>[]

    if (!data.length) return []

    const [commodities, commodityCatalogues, railcars] = await Promise.all([
      this.getBillingCommodities(),
      this.getBillingCommodityCatalogues(),
      visitType === CarrierType.Train ? this.getRailcars() : Promise.resolve([]),
    ])

    const requiredHeaders = [
      'Commodity Code',
      'Reference Number',
      'Planned Quantity',
      'Total Gross Weight',
      'Weight Unit',
    ]
    const missingHeaders = requiredHeaders.filter(header => !(header in data[0]))
    if (missingHeaders.length) {
      throw new FileFormatError(`Missing required columns: ${missingHeaders.join(', ')}`)
    }

    const railcarMap = new Map(
      (relatedRailcars ?? []).map(railcar => [railcar.railcarName.trim().toLowerCase(), railcar]),
    )
    const railcarsByName = new Map(
      railcars.map(railcar => [railcar.name.trim().toLowerCase(), railcar]),
    )
    const catalogueByName = new Map<string, IBillingCommodityCatalogue>(
      commodityCatalogues.map(catalogue => [catalogue.name.trim().toLowerCase(), catalogue]),
    )
    const defaultCatalogue = commodityCatalogues[0]
    const hasMultipleCatalogues = commodityCatalogues.length > 1

    const rows = await Promise.all(
      data.map(async (row, index) => {
        const rowIndex = index + 2
        const commodityCode = this.toCellString(row['Commodity Code']).trim()
        if (!commodityCode) {
          throw new FileFormatError(`Commodity Code is required at row ${rowIndex}`)
        }

        const commodityCatalogueName =
          this.toCellString(row['Commodity Catalogue']).trim() || undefined
        if (hasMultipleCatalogues && !commodityCatalogueName) {
          throw new FileFormatError(
            `Commodity Catalogue is required at row ${rowIndex} because multiple catalogues exist`,
          )
        }

        const selectedCatalogue = commodityCatalogueName
          ? catalogueByName.get(commodityCatalogueName.toLowerCase())
          : defaultCatalogue

        if (commodityCatalogueName && !selectedCatalogue) {
          throw new FileFormatError(
            `Commodity Catalogue "${commodityCatalogueName}" was not found at row ${rowIndex}`,
          )
        }

        if (!selectedCatalogue) {
          throw new FileFormatError(
            `Cannot resolve Commodity Code "${commodityCode}" at row ${rowIndex}`,
          )
        }

        const commodity: IBillingCommodity | undefined = commodities.find(
          item =>
            item.catalogueId === selectedCatalogue.id &&
            item.code.toLowerCase() === commodityCode.toLowerCase(),
        )

        if (!commodity) {
          throw new FileFormatError(
            `Could not resolve Commodity Code "${commodityCode}" in catalogue "${selectedCatalogue.name}" at row ${rowIndex}`,
          )
        }

        const referenceNumber = this.toCellString(row['Reference Number']).trim()
        if (!referenceNumber) {
          throw new FileFormatError(`Reference Number is required at row ${rowIndex}`)
        }

        const plannedAmount = this.parsePositiveInteger(
          row['Planned Quantity'],
          'Planned Quantity',
          rowIndex,
        )
        const grossWeightAsString = this.toCellString(row['Total Gross Weight']).trim()
        if (!grossWeightAsString) {
          throw new FileFormatError(`Total Gross Weight is required at row ${rowIndex}`)
        }

        const grossWeight = Number(grossWeightAsString)
        if (Number.isNaN(grossWeight)) {
          throw new FileFormatError(`Total Gross Weight must be numeric at row ${rowIndex}`)
        }
        if (grossWeight < 0) {
          throw new FileFormatError(
            `Total Gross Weight must be greater than or equal to 0 at row ${rowIndex}`,
          )
        }

        const weightUnitRaw = this.toCellString(row['Weight Unit']).trim()
        if (!weightUnitRaw) {
          throw new FileFormatError(`Weight Unit is required at row ${rowIndex}`)
        }

        const weightUnit = this.mapWeightUnit(weightUnitRaw)
        const imoClasses = this.parseSlashOrPipeSeparatedArray(
          this.toCellString(row['IMO Classes']),
        )
        const invalidImoClass = imoClasses.find(imoClass => !ImdgCodes.includes(imoClass))
        if (invalidImoClass) {
          throw new FileFormatError(`Unsupported IMO Class "${invalidImoClass}" at row ${rowIndex}`)
        }

        const identifiableItems = this.parseSlashOrPipeSeparatedArray(this.toCellString(row['IDs']))
        if (identifiableItems.length > 0 && identifiableItems.length !== plannedAmount) {
          throw new FileFormatError(
            `IDs count (${identifiableItems.length}) does not match Planned Quantity (${plannedAmount}) at row ${rowIndex}`,
          )
        }

        const railTrackName = this.toCellString(row['Rail Track']).trim()
        const railcarNumber = this.toCellString(row['Rail Car Number']).trim()

        if (visitType === CarrierType.Train && !railTrackName) {
          throw new FileFormatError(`Rail Track is required at row ${rowIndex}`)
        }
        if (visitType === CarrierType.Train && !railcarNumber) {
          throw new FileFormatError(`Rail Car Number is required at row ${rowIndex}`)
        }

        const railcar = railcarNumber ? railcarMap.get(railcarNumber.toLowerCase()) : undefined
        const railcarFromMaster = railcarNumber
          ? railcarsByName.get(railcarNumber.toLowerCase())
          : undefined
        const railTrackId = railTrackName
          ? railTrackNameMap?.get(railTrackName.toLocaleLowerCase())?.id
          : undefined

        if (visitType === CarrierType.Train && railcarNumber && !railcar && !railcarFromMaster) {
          throw new FileFormatError(`Unknown Rail Car Number "${railcarNumber}" at row ${rowIndex}`)
        }

        if (visitType === CarrierType.Train && railTrackName && !railTrackId) {
          throw new FileFormatError(`Unknown Rail Track "${railTrackName}" at row ${rowIndex}`)
        }

        if (
          visitType === CarrierType.Train &&
          railcar?.railTrackId &&
          railTrackId &&
          railcar.railTrackId !== railTrackId
        ) {
          const existingTrack = railcar.railTrackName || railcar.railTrackId
          throw new FileFormatError(
            `Rail Car Number "${railcarNumber}" is already set on track "${existingTrack}" and cannot be set on track "${railTrackName}" at row ${rowIndex}`,
          )
        }

        return {
          commodityId: commodity.id,
          commodityName: commodity.name,
          referenceNumber,
          lotNumber: this.toCellString(row['Lot Number']).trim() || undefined,
          plannedAmount,
          grossWeight,
          weightUnit,
          imoClasses,
          identifiableItems,
          railTrackId: railcar?.railTrackId ?? railTrackId,
          railcarId: railcar?.railcarId ?? railcarFromMaster?.id,
          railTrackName: railcar?.railTrackName ?? (railTrackName || undefined),
          railcarNumber:
            railcar?.railcarName ?? railcarFromMaster?.name ?? (railcarNumber || undefined),
        }
      }),
    )

    return rows
  }
}

const generalCargoOrderUploadParsingService = new GeneralCargoOrderUploadParsingService()

export default generalCargoOrderUploadParsingService
